import { FC, useState } from 'react';

interface TranscriptionCompleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDownload: () => void;
  onDelete?: () => void;
  transcriptionTitle: string;
  transcriptionText: string;
}

const TranscriptionCompleteModal: FC<TranscriptionCompleteModalProps> = ({
  isOpen,
  onClose,
  onDownload,
  onDelete,
  transcriptionTitle,
  transcriptionText,
}) => {
  const [copied, setCopied] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  if (!isOpen) return null;

  // Handle copy to clipboard
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(transcriptionText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000); // Reset after 2 seconds
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  // Split transcription into paragraphs for better display
  const paragraphs = transcriptionText.split('\n\n');

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    >
      <dialog
        open
        className="bg-white relative flex flex-col border-0 p-0 max-w-none max-h-none m-0"
        aria-labelledby="modal-title"
        style={{
          width: '795px',
          height: '590px',
          borderRadius: '12px',
          paddingRight: '18px',
          paddingBottom: '42px',
          paddingLeft: '18px',
          gap: '16px',
          opacity: 1,
        }}
      >
        {/* Header with Title and Close Button */}
        <div className="flex justify-between items-start pt-6 pb-2">
          <h2 
            id="modal-title"
            className="text-gray-900 flex-1"
            style={{
              fontFamily: 'Maven Pro, sans-serif',
              fontSize: '24px',
              fontWeight: 700,
              lineHeight: '1.3',
            }}
          >
            {transcriptionTitle}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors ml-4 flex-shrink-0 bg-transparent border-0 p-0"
            aria-label="Close"
            style={{ background: 'transparent' }}
          >
            <svg 
              className="w-6 h-6" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M6 18L18 6M6 6l12 12" 
              />
            </svg>
          </button>
        </div>

        {/* Transcription Content with Timestamps */}
        <div 
          className="flex-1 overflow-y-auto bg-white rounded-lg p-6 border border-gray-200 relative"
          style={{
            fontFamily: 'Maven Pro, sans-serif',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          }}
        >
          {/* Copy Button - Top Right Corner */}
          <button
            onClick={handleCopy}
            className="absolute top-4 right-4 text-gray-600 hover:text-gray-800 transition-colors bg-white border-0 p-1.5 rounded-md hover:bg-gray-50"
            aria-label="Copy transcription"
            title={copied ? "Copied!" : "Copy all text"}
            style={{ background: 'white', zIndex: 10 }}
          >
            {copied ? (
              <svg 
                className="w-5 h-5 text-green-600" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M5 13l4 4L19 7" 
                />
              </svg>
            ) : (
              <svg 
                className="w-5 h-5" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" 
                />
              </svg>
            )}
          </button>

          {/* Transcription content - Display actual transcription text */}
          <div className="space-y-4">
            <div 
              className="text-gray-700 space-y-3"
              style={{
                fontSize: '14px',
                lineHeight: '1.6',
              }}
            >
              {paragraphs.map((para, index) => (
                <p key={`para-${index}-${para.substring(0, 20)}`}>{para}</p>
              ))}
            </div>
          </div>
        </div>

        {/* Footer Buttons */}
        <div className="flex justify-between items-center gap-3 pt-4">
          {/* Delete Button - Left Side */}
          {onDelete && (
            <button
              onClick={async () => {
                setIsDeleting(true);
                try {
                  await Promise.resolve(onDelete());
                } finally {
                  setIsDeleting(false);
                }
              }}
              disabled={isDeleting}
              className="transition-all"
              style={{
                width: '60px',
                height: '60px',
                borderRadius: '21px',
                border: '1px solid #EF4444',
                padding: '14px',
                gap: '10px',
                opacity: isDeleting ? 0.6 : 1,
                background: 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: isDeleting ? 'not-allowed' : 'pointer',
              }}
              onMouseEnter={(e) => {
                if (!isDeleting) {
                  e.currentTarget.style.background = "#F9FAFB";
                }
              }}
              onMouseLeave={(e) => {
                if (!isDeleting) {
                  e.currentTarget.style.background = "transparent";
                }
              }}
              aria-label="Delete"
            >
              {isDeleting ? (
                <svg 
                  className="w-5 h-5 text-red-500 animate-spin" 
                  fill="none" 
                  viewBox="0 0 24 24"
                >
                  <circle 
                    className="opacity-25" 
                    cx="12" 
                    cy="12" 
                    r="10" 
                    stroke="currentColor" 
                    strokeWidth="4"
                  />
                  <path 
                    className="opacity-75" 
                    fill="currentColor" 
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              ) : (
                <svg 
                  className="w-full h-full text-red-500" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" 
                  />
                </svg>
              )}
            </button>
          )}

          {/* Right Side Buttons */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-6 py-3 rounded-full font-medium transition-all"
              style={{
                fontFamily: 'Maven Pro, sans-serif',
                fontSize: '16px',
                fontWeight: 600,
                border: '2px solid #E5E7EB',
                background: '#FFFFFF',
                color: '#6B7280',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#F9FAFB";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#FFFFFF";
              }}
            >
              Close
            </button>
            <button
              onClick={onDownload}
              className="px-6 py-3 rounded-full text-white font-medium transition-all hover:opacity-90 flex items-center gap-2"
              style={{
                fontFamily: 'Maven Pro, sans-serif',
                fontSize: '16px',
                fontWeight: 600,
                background: 'linear-gradient(135deg, #1F2937 0%, #1F2937 100%)',
              }}
            >
              <svg 
                className="w-5 h-5" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" 
                />
              </svg>
              Download
            </button>
          </div>
        </div>
      </dialog>
    </div>
  );
};

export default TranscriptionCompleteModal;
